// connectCall — mint a LiveKit token, build a Room, connect, wire events.
// Called from RoomList.MeetRoomRow onClick, Main.Join onClick, NewRoomModal
// submit, etc — anywhere the user enters a call view.
//
// Kept as a registered project function (not a page-level lifecycle hook)
// because DOMQL v3.14 doesn't fire onCreate on `if:`/`extends:` children and
// doesn't fire onUpdate on root state changes. Chat/calendar follow the
// same "imperative action functions, no lifecycle hooks on sub-views"
// pattern.
//
// LiveKit Room instance is stored in a MODULE-LEVEL variable, NOT in
// smbls reactive state. Passing the Room through `root.update({...})`
// strips class methods (the reactive proxy serializes values), so
// `room.disconnect()` fails on subsequent reads. The module variable
// preserves the live instance with methods intact. Components/functions
// that need the Room import `getLivekitRoom()`.
import { RoomEvent } from 'livekit-client'
import { buildLivekitRoom } from './livekitConnect.js'

let _livekitRoom = null

export const getLivekitRoom = () => _livekitRoom

// Filter out the meet-transcriber agent — LiveKit assigns it an identity
// like `agent-AJ_<jobId>` when AgentDispatchClient dispatches a named agent.
// It's listen-only (no tracks published) and should be invisible in the
// human-facing participant grid.
const _isAgent = (identity) => typeof identity === 'string' && identity.startsWith('agent-')

const _refreshParticipants = (root, livekitRoom) => {
  const local = livekitRoom.localParticipant
  const remoteValues = typeof livekitRoom.remoteParticipants?.values === 'function'
    ? livekitRoom.remoteParticipants.values()
    : Object.values(livekitRoom.remoteParticipants || {})
  const remote = Array.from(remoteValues).filter(p => !_isAgent(p.identity))
  // `id` must be stable so smbls can diff the children array and REUSE
  // the existing tile element instead of recreating it on every mic /
  // speaker event. Without a stable id, each refresh spawns fresh
  // <video> DOM nodes and the user sees a flicker every time anyone
  // speaks. Use participant.identity as the id.
  const all = [
    {
      id: local.identity,
      identity: local.identity,
      name: local.name || local.identity,
      isLocal: true,
      isMicEnabled: local.isMicrophoneEnabled,
      isCameraEnabled: local.isCameraEnabled
    },
    ...remote.map(p => ({
      id: p.identity,
      identity: p.identity,
      name: p.name || p.identity,
      isLocal: false,
      isMicEnabled: p.isMicrophoneEnabled,
      isCameraEnabled: p.isCameraEnabled
    }))
  ]
  root.update({ livekitParticipants: all })
}

// Active-speaker state lives in a separate slot so speaker-change events
// don't invalidate the participants array diff. Components that want a
// speaker ring border read from root.livekitSpeakers directly.
export const _refreshSpeakers = (root, livekitRoom) => {
  const speakers = livekitRoom.activeSpeakers || []
  const set = {}
  for (const p of speakers) set[p.identity] = true
  root.update({ livekitSpeakers: set })
}

export const connectCall = async function (roomId) {
  const root = this.state.root
  if (!roomId) roomId = root.activeRoomId
  if (!roomId) {
    console.error('[connectCall] no roomId')
    return null
  }
  if (_livekitRoom) {
    console.log('[connectCall] already connected')
    return _livekitRoom
  }
  if (root.meetCallInFlight) {
    console.log('[connectCall] already connecting')
    return null
  }

  root.update({ meetCallInFlight: true, meetCallError: null, livekitConnected: false })

  // Re-scope realtime to include meet_transcripts (and analyses) for this
  // specific room. Without this, MeetLiveCaptions never receives INSERT
  // events because the page-level subscription only covers meet_rooms /
  // meet_participants / meet_waiting_room. Scoping by roomId keeps the
  // stream tight — we don't want every utterance from every concurrent
  // call in the org flowing through the channel.
  this.call('subscribeMeetRealtime', {
    tables: ['meet_rooms', 'meet_participants', 'meet_waiting_room', 'meet_transcripts', 'meet_transcript_analyses'],
    roomId
  })

  try {
    const tokenInfo = await this.call('mintLivekitToken', roomId)
    const startingCount = (root.liveParticipants?.[roomId] || []).filter(p => !p.leftAt).length || 1
    const livekitRoom = buildLivekitRoom(startingCount)

    const refresh = () => _refreshParticipants(root, livekitRoom)
    // Participant-set events only — events that can change WHO is in the
    // room or what tracks exist.
    livekitRoom.on(RoomEvent.ParticipantConnected, refresh)
    livekitRoom.on(RoomEvent.ParticipantDisconnected, refresh)
    livekitRoom.on(RoomEvent.LocalTrackPublished, refresh)
    livekitRoom.on(RoomEvent.LocalTrackUnpublished, refresh)
    livekitRoom.on(RoomEvent.TrackSubscribed, refresh)
    livekitRoom.on(RoomEvent.TrackUnsubscribed, refresh)
    // Mute toggles change isMicEnabled/isCameraEnabled — also a full
    // refresh. These fire on user action, not per-frame, so the diff
    // stays stable.
    livekitRoom.on(RoomEvent.TrackMuted, refresh)
    livekitRoom.on(RoomEvent.TrackUnmuted, refresh)
    // ActiveSpeakersChanged fires on EVERY spoken syllable. Route to a
    // separate state slot (livekitSpeakers) so updating it doesn't
    // invalidate the livekitParticipants diff and tear down tile DOM.
    livekitRoom.on(RoomEvent.ActiveSpeakersChanged, () => _refreshSpeakers(root, livekitRoom))
    livekitRoom.on(RoomEvent.Disconnected, () => {
      console.log('[meet] livekit disconnected')
      _livekitRoom = null
      root.update({ livekitParticipants: [], livekitConnected: false })
    })

    await livekitRoom.connect(tokenInfo.livekitUrl, tokenInfo.livekitToken)
    // Publish the local mic + camera tracks. Errors here mean the browser
    // denied permission or there's no device — surface the error instead
    // of silently continuing with no tracks (which was the old bug).
    try {
      await livekitRoom.localParticipant.setMicrophoneEnabled(true)
    } catch (err) {
      console.error('[connectCall] mic enable failed (permission denied?)', err)
    }
    try {
      await livekitRoom.localParticipant.setCameraEnabled(true)
    } catch (err) {
      console.error('[connectCall] camera enable failed (permission denied?)', err)
    }

    _livekitRoom = livekitRoom
    root.update({ livekitConnected: true, meetCallInFlight: false })
    _refreshParticipants(root, livekitRoom)
    return livekitRoom
  } catch (err) {
    console.error('[connectCall] failed', err)
    _livekitRoom = null
    root.update({
      meetCallError: err?.message || 'connect failed',
      meetCallInFlight: false,
      livekitConnected: false
    })
    return null
  }
}

export const disconnectCall = async function () {
  const root = this.state.root
  // Re-scope realtime back to the broad list view — drops the transcripts
  // + analyses subscriptions so we don't keep streaming utterances for a
  // room we're no longer in.
  try {
    this.call('subscribeMeetRealtime', {
      tables: ['meet_rooms', 'meet_participants', 'meet_waiting_room']
    })
  } catch (err) {
    console.warn('[disconnectCall] rescope failed', err)
  }
  const room = _livekitRoom
  if (!room) {
    root.update({ livekitParticipants: [], livekitConnected: false })
    return
  }
  try {
    await room.disconnect()
  } catch (err) {
    console.warn('[disconnectCall] failed', err)
  }
  _livekitRoom = null
  root.update({ livekitParticipants: [], livekitConnected: false })
}
